import React, { useState, useEffect, useCallback } from 'react';
import { adminGet } from '../data/api';

const card = { background: '#0F0F11', border: '1px solid #27272A' };
const mono = { fontFamily: 'JetBrains Mono, ui-monospace, monospace' };

function Pill({ color = 'zinc', children, testid }) {
  const palette = {
    zinc: ['#3F3F46', '#A1A1AA'],
    emerald: ['#064E3B', '#6EE7B7'],
    rose: ['#7F1D1D', '#FCA5A5'],
    amber: ['#78350F', '#FCD34D'],
    sky: ['#0C4A6E', '#7DD3FC'],
  }[color] || ['#3F3F46', '#A1A1AA'];
  return (
    <span data-testid={testid}
      className="text-[9px] px-1.5 py-0.5 rounded-sm border"
      style={{ background: palette[0] + '40', borderColor: palette[0],
                color: palette[1], ...mono }}>
      {children}
    </span>
  );
}

function fmtPct(v, digits = 2) {
  if (v == null) return '—';
  return (Number(v) * 100).toFixed(digits) + '%';
}
function fmtSigned(v, suffix = '') {
  if (v == null) return '—';
  const n = Number(v);
  return (n >= 0 ? '+' : '') + n.toFixed(2) + suffix;
}

export default function Form48hValidationPanel({ pin }) {
  const [days, setDays] = useState(14);
  const [minShift, setMinShift] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const path = `/tt/admin/setscore-form48h-validation?days=${days}&min_shift_pp=${minShift}&limit=2000`;
      const j = await adminGet(path, pin || '');
      setData(j);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [days, minShift, pin]);

  useEffect(() => { load(); }, [load]);

  const m = data?.metrics || {};
  const acc = m.accuracy || {};
  const br = m.brier || {};
  const hh = m.helped_hurt || {};
  const fl = m.flips || {};
  const sm = data?.summary || {};

  return (
    <div className="rounded-sm p-4 mt-3" style={card} data-testid="form48h-validation-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500" style={mono}>
            Form 48h Validation
          </div>
          <div className="text-[10px] text-zinc-600 italic mt-0.5" style={mono}>
            Read-only audit. Compares Raw SetScore vs Form 48h Adjusted on settled matches. Does NOT enable any setting.
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px]" style={mono}>
          <label className="text-zinc-500">days</label>
          <select data-testid="form48h-days-select" value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="bg-zinc-900 border border-zinc-700 px-2 py-1 rounded-sm">
            <option value={7}>7</option>
            <option value={14}>14</option>
            <option value={30}>30</option>
          </select>
          <label className="text-zinc-500 ml-2">min shift pp</label>
          <select data-testid="form48h-min-shift-select" value={minShift}
            onChange={e => setMinShift(Number(e.target.value))}
            className="bg-zinc-900 border border-zinc-700 px-2 py-1 rounded-sm">
            <option value={0}>0</option>
            <option value={2}>2</option>
            <option value={5}>5</option>
          </select>
          <button data-testid="form48h-refresh-btn" onClick={load} disabled={loading}
            className="ml-2 px-2 py-1 rounded-sm border border-zinc-700 text-zinc-200 hover:bg-zinc-800 disabled:opacity-50">
            {loading ? '…' : 'refresh'}
          </button>
        </div>
      </div>

      {err && (
        <div className="text-[11px] text-rose-300 mb-2" data-testid="form48h-error">
          {err}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[11px]" style={mono}>
        <KPI label="analyzed" value={sm.analyzed} testid="kpi-analyzed" />
        <KPI label="settled" value={sm.settled} testid="kpi-settled" />
        <KPI label="skipped (no stack)" value={sm.skipped_missing_stack} testid="kpi-skipped-stack" />
        <KPI label="skipped (no result)" value={sm.skipped_missing_result} testid="kpi-skipped-result" />
        <KPI label="avg |shift|" value={sm.avg_abs_shift_pp != null ? sm.avg_abs_shift_pp.toFixed(2) + ' pp' : '—'} testid="kpi-avg-abs-shift" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px] mt-2" style={mono}>
        <div className="rounded-sm p-2" style={{ background: '#18181B' }}>
          <div className="text-[9px] text-zinc-500">accuracy raw → form48h</div>
          <div data-testid="kpi-accuracy" className="text-zinc-100 text-sm">
            {fmtPct(acc.raw_accuracy)} → {fmtPct(acc.form48h_accuracy)}
            {acc.delta_accuracy_pp != null && (
              <span className={acc.delta_accuracy_pp >= 0 ? 'text-emerald-300' : 'text-rose-300'}>
                {' '}({fmtSigned(acc.delta_accuracy_pp, ' pp')})
              </span>
            )}
          </div>
        </div>
        <div className="rounded-sm p-2" style={{ background: '#18181B' }}>
          <div className="text-[9px] text-zinc-500">brier raw → form48h</div>
          <div data-testid="kpi-brier" className="text-zinc-100 text-sm">
            {br.brier_raw != null ? br.brier_raw.toFixed(4) : '—'} →{' '}
            {br.brier_form48h != null ? br.brier_form48h.toFixed(4) : '—'}
            {br.brier_delta != null && (
              <span className={br.brier_delta <= 0 ? 'text-emerald-300' : 'text-rose-300'}>
                {' '}({fmtSigned(br.brier_delta)})
              </span>
            )}
          </div>
          <div className="text-[9px] text-zinc-500">negative delta = improvement</div>
        </div>
        <div className="rounded-sm p-2" style={{ background: '#18181B' }}>
          <div className="text-[9px] text-zinc-500">helped / hurt / neutral</div>
          <div data-testid="kpi-helped-hurt" className="text-zinc-100 text-sm">
            <span className="text-emerald-300">{hh.helped_count ?? 0}</span>
            {' / '}
            <span className="text-rose-300">{hh.hurt_count ?? 0}</span>
            {' / '}
            <span className="text-zinc-400">{hh.neutral_count ?? 0}</span>
            <span className="text-zinc-500"> · net {hh.net_helped_minus_hurt ?? 0}</span>
          </div>
        </div>
        <div className="rounded-sm p-2" style={{ background: '#18181B' }}>
          <div className="text-[9px] text-zinc-500">flips correct / wrong</div>
          <div data-testid="kpi-flips" className="text-zinc-100 text-sm">
            <span className="text-emerald-300">{fl.flips_correct ?? 0}</span>
            {' / '}
            <span className="text-rose-300">{fl.flips_wrong ?? 0}</span>
            {fl.flip_accuracy != null && (
              <span className="text-zinc-500"> · {fmtPct(fl.flip_accuracy)} acc</span>
            )}
          </div>
        </div>
        <div className="rounded-sm p-2" style={{ background: '#18181B' }}>
          <div className="text-[9px] text-zinc-500">logloss raw → form48h</div>
          <div data-testid="kpi-logloss" className="text-zinc-100 text-sm">
            {m.logloss?.logloss_raw != null ? m.logloss.logloss_raw.toFixed(4) : '—'} →{' '}
            {m.logloss?.logloss_form48h != null ? m.logloss.logloss_form48h.toFixed(4) : '—'}
            {m.logloss?.logloss_delta != null && (
              <span className={m.logloss.logloss_delta <= 0 ? 'text-emerald-300' : 'text-rose-300'}>
                {' '}({fmtSigned(m.logloss.logloss_delta)})
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Breakdowns */}
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
          <BreakdownTable
            title="By shift bucket"
            rows={data.breakdowns?.by_shift_bucket || []}
            keyField="bucket"
            testid="bd-by-shift-bucket"
          />
          <BreakdownTable
            title="By current_form_source"
            rows={data.breakdowns?.by_current_form_source || []}
            keyField="source"
            testid="bd-by-source"
          />
        </div>
      )}

      {/* Cases */}
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
          <CaseTable title="Dangerous (form hurt the most)"
            rows={data.cases?.dangerous_hurt_top || []}
            testid="cases-dangerous"
          />
          <CaseTable title="Best helped"
            rows={data.cases?.best_helped_top || []}
            testid="cases-best-helped"
          />
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, testid }) {
  return (
    <div className="rounded-sm p-2" style={{ background: '#18181B', ...mono }}>
      <div className="text-[9px] text-zinc-500">{label}</div>
      <div data-testid={testid} className="text-zinc-100 text-sm">{value ?? '—'}</div>
    </div>
  );
}

function BreakdownTable({ title, rows, keyField, testid }) {
  return (
    <div className="rounded-sm p-2" style={{ background: '#0B0B0E', border: '1px solid #27272A' }} data-testid={testid}>
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2" style={mono}>
        {title}
      </div>
      <table className="w-full text-[10px]" style={mono}>
        <thead className="text-zinc-500">
          <tr>
            <th className="text-left">{keyField}</th>
            <th className="text-right">n</th>
            <th className="text-right">raw acc</th>
            <th className="text-right">form acc</th>
            <th className="text-right">brier Δ</th>
            <th className="text-right">helped</th>
            <th className="text-right">hurt</th>
          </tr>
        </thead>
        <tbody className="text-zinc-200">
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-zinc-800">
              <td>{r[keyField]}</td>
              <td className="text-right">{r.n}</td>
              <td className="text-right">{fmtPct(r.raw_accuracy)}</td>
              <td className="text-right">{fmtPct(r.form48h_accuracy)}</td>
              <td className={`text-right ${r.brier_delta != null && r.brier_delta < 0 ? 'text-emerald-300' : (r.brier_delta != null && r.brier_delta > 0 ? 'text-rose-300' : 'text-zinc-400')}`}>
                {r.brier_delta != null ? fmtSigned(r.brier_delta) : '—'}
              </td>
              <td className="text-right text-emerald-300">{r.helped}</td>
              <td className="text-right text-rose-300">{r.hurt}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={7} className="text-zinc-500 italic py-2">no data</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function CaseTable({ title, rows, testid }) {
  return (
    <div className="rounded-sm p-2" style={{ background: '#0B0B0E', border: '1px solid #27272A' }} data-testid={testid}>
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2" style={mono}>
        {title}
      </div>
      <table className="w-full text-[10px]" style={mono}>
        <thead className="text-zinc-500">
          <tr>
            <th className="text-left">match</th>
            <th className="text-right">winner</th>
            <th className="text-right">raw → form</th>
            <th className="text-right">shift</th>
            <th className="text-right">source</th>
            <th className="text-right">strength</th>
          </tr>
        </thead>
        <tbody className="text-zinc-200">
          {rows.slice(0, 25).map((r, i) => (
            <tr key={i} className="border-t border-zinc-800">
              <td className="truncate max-w-[200px]">{r.p1_name} vs {r.p2_name}</td>
              <td className="text-right">{r.actual_winner}</td>
              <td className="text-right">{fmtPct(r.raw_p1, 1)} → {fmtPct(r.form_p1, 1)}</td>
              <td className="text-right">{fmtSigned(r.shift_pp, ' pp')}</td>
              <td className="text-right">{r.current_form_source}</td>
              <td className="text-right">
                <Pill color={r.current_form_strength === 'strong' ? 'emerald' : (r.current_form_strength === 'cold' ? 'rose' : 'zinc')}>
                  {r.current_form_strength}
                </Pill>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={6} className="text-zinc-500 italic py-2">no data</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
